import { ElMessage, ElMessageBox } from 'element-plus';

/**
 * Notifications standardisées pour les opérations CRUD
 */
export const notify = {
  /**
   * Succès de création
   * @param {string} entityType - Type d'entité (ex: "utilisateur", "rôle")
   * @param {string} entityName - Nom de l'entité créée
   */
  created(entityType, entityName = '') {
    const name = entityName ? ` "${entityName}"` : '';
    ElMessage.success({
      message: `${entityType}${name} créé avec succès`,
      duration: 5000
    });
  },

  /**
   * Succès de mise à jour
   * @param {string} entityType - Type d'entité
   * @param {string} entityName - Nom de l'entité mise à jour
   */
  updated(entityType, entityName = '') {
    const name = entityName ? ` "${entityName}"` : '';
    ElMessage.success({
      message: `${entityType}${name} mis à jour avec succès`,
      duration: 5000
    });
  },

  /**
   * Succès de suppression
   * @param {string} entityType - Type d'entité
   * @param {string} entityName - Nom de l'entité supprimée
   */
  deleted(entityType, entityName = '') {
    const name = entityName ? ` "${entityName}"` : '';
    ElMessage.success({
      message: `${entityType}${name} supprimé avec succès`,
      duration: 5000
    });
  },

  /**
   * Message de succès personnalisé
   * @param {string} message - Message à afficher
   */
  success(message) {
    ElMessage.success({
      message,
      duration: 5000
    });
  },

  /**
   * Message d'erreur
   * @param {string} message - Message d'erreur
   */
  error(message = 'Une erreur est survenue') {
    ElMessage.error({
      message,
      duration: 5000
    });
  },

  /**
   * Message d'information
   * @param {string} message - Message d'information
   */
  info(message) {
    ElMessage.info({
      message,
      duration: 5000
    });
  },

  /**
   * Message d'avertissement
   * @param {string} message - Message d'avertissement
   */
  warning(message) {
    ElMessage.warning({
      message,
      duration: 5000
    });
  }
};

/**
 * Dialogues de confirmation standardisés
 */
export const confirm = {
  /**
   * Confirmation de suppression
   * @param {string} entityType - Type d'entité
   * @param {string} entityName - Nom de l'entité à supprimer
   * @param {Object} options - Options supplémentaires
   * @returns {Promise}
   */
  async delete(entityType, entityName, options = {}) {
    return ElMessageBox.confirm(
      `Êtes-vous sûr de vouloir supprimer ${entityType} "${entityName}" ?`,
      'Confirmation de suppression',
      {
        confirmButtonText: 'Supprimer',
        cancelButtonText: 'Annuler',
        type: 'warning',
        dangerouslyUseHTMLString: true,
        ...options
      }
    );
  },

  /**
   * Confirmation personnalisée
   * @param {string} message - Message de confirmation
   * @param {string} title - Titre du dialogue
   * @param {Object} options - Options supplémentaires
   * @returns {Promise}
   */
  async custom(message, title = 'Confirmation', options = {}) {
    return ElMessageBox.confirm(
      message,
      title,
      {
        confirmButtonText: 'Confirmer',
        cancelButtonText: 'Annuler',
        type: 'warning',
        ...options
      }
    );
  }
};

/**
 * Helper pour gérer les erreurs d'API
 * @param {Error} error - Erreur à traiter
 * @param {string} defaultMessage - Message par défaut
 * @deprecated Utiliser handleError de '@/utils/errorHandler' à la place
 */
export function handleApiError(error, defaultMessage = 'Une erreur est survenue') {
  // Support du nouveau format d'erreur enrichi
  if (error.validationErrors) {
    return error.validationErrors;
  }
  if (error.response?.data?.errors) {
    // Erreurs de validation Laravel
    return error.response.data.errors;
  } else if (error.response?.data?.message) {
    // Message d'erreur spécifique
    notify.error(error.response.data.message);
  } else if (error.message) {
    // Message d'erreur générique
    notify.error(error.message);
  } else {
    // Message par défaut
    notify.error(defaultMessage);
  }
  return null;
}

// Re-export du nouveau système pour faciliter la migration
export { handleError, safeApiCall, createErrorHandler } from '@/utils/errorHandler';
